import {Box, Group, Accordion, Alert, Progress, Text, createStyles} from '@mantine/core';
import React from 'react';

import {TablePerformanceSummary} from 'notebook/performance/tableLimitUtils';
import {GRAY_LIGHTER, BLUE_LIGHTER, GRAY_LIGHT} from 'ui-library/Colors';
import {Title} from 'ui-library/Headers';
import sortedBy from 'util/sortedBy';

import {ComputedModalRowInfo} from './performanceMessagesUtil';

interface TablePerformanceRowProps {
  row: TablePerformanceSummary;
  computedRowInfo: ComputedModalRowInfo;
  showStepNameColumn: boolean;
}

function TablePerformanceRow({
  row,
  computedRowInfo,
  showStepNameColumn,
}: TablePerformanceRowProps): JSX.Element {
  const {classes} = useStyles();

  const {markerColor, alertTitle, styleMessageAsAlert, message, pct} = computedRowInfo;
  return (
    <Accordion.Item key={row.tableId} value={row.tableId}>
      <Accordion.Control>
        <Group>
          <Box className={classes.coloredMarker} style={{backgroundColor: markerColor}} />
          <Box className={classes.flexItem}>
            <div className={classes.nameText}>{row.name}</div>
          </Box>
          {showStepNameColumn && (
            <Box className={classes.flexItem}>
              <div className={classes.nameText}>{row.documentName}</div>
            </Box>
          )}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Group>
          {/* Include an empty, uncolored marker just to make spacing consistent */}
          <Box className={classes.coloredMarker} />
          <Box className={classes.flexItem}>
            <Group>
              <Title>Share of total entry performance:</Title>
              <Box className={classes.flexItem}>
                <Progress
                  value={pct * 100}
                  color={BLUE_LIGHTER}
                  size={16}
                  radius="md"
                  className={classes.progressBar}
                />
              </Box>
              <Text className={classes.percentageLabel}>{Math.round(pct * 100)}%</Text>
            </Group>
            {styleMessageAsAlert ? (
              <Alert title={alertTitle}>
                <Text>{message}</Text>
                <Text>Cell Count: {row.numCells}</Text>
              </Alert>
            ) : (
              <Box>
                <Text>{message}</Text>
                <Text>Cell Count: {row.numCells}</Text>
              </Box>
            )}
          </Box>
        </Group>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

interface TablePerformanceSummaryTableProps {
  tableSummaries: Array<TablePerformanceSummary>;
  tables: Record<string, ComputedModalRowInfo>;
  showStepNameColumn: boolean;
}

export function TablePerformanceSummaryTable({
  tableSummaries,
  tables: computedTableInfo,
  showStepNameColumn,
}: TablePerformanceSummaryTableProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();

  const sortedRows = sortedBy(tableSummaries, [
    {
      accessor: (summary: TablePerformanceSummary): number => summary.formulaCost,
      reversed: true,
    },
  ]);

  return (
    <>
      <Box className={classes.performanceImpactTable}>
        <Group className={classes.performanceImpactTableHeader}>
          <Box className={classes.flexItem}>
            <Title>Tables in this entry</Title>
          </Box>
          {showStepNameColumn && (
            <Box className={classes.flexItem}>
              <Title>Step Name</Title>
            </Box>
          )}
        </Group>
        <Accordion chevronPosition="left" variant="contained">
          {sortedRows.map((row) => (
            <TablePerformanceRow
              key={row.tableId}
              row={row}
              computedRowInfo={computedTableInfo[row.tableId]}
              showStepNameColumn={showStepNameColumn}
            />
          ))}
        </Accordion>
      </Box>
    </>
  );
}

const useStyles = createStyles(() => ({
  nameText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  percentageLabel: {
    width: '30px',
    marginBottom: '2px',
  },
  performanceImpactTable: {
    border: `1px solid ${GRAY_LIGHT}`,
  },
  performanceImpactTableHeader: {
    background: GRAY_LIGHTER,
    padding: '5px',
  },
  flexItem: {
    flex: 1,
  },
  performanceImpactTooltipIcon: {
    marginBottom: '2px',
  },
  coloredMarker: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  progressBar: {
    marginBottom: '2px',
  },
}));

export const _forTesting = {
  TablePerformanceRow,
};
